import { useCallback } from 'react';

import { Dropzone, Preview } from 'apps/shared/scripts/components';
import { useUploadImage } from 'apps/shared/scripts/hooks';

import withControl, { WithControlRenderProps } from '../withControl';

import { UploaderProps } from './types';

type Props = UploaderProps & WithControlRenderProps;

const Uploader = ({
  className = '',
  onChange,
  onError,
  toFormValue,
  fromFormValue,
  value,
}: Props) => {
  const { isUploading, onUploadImage, percentage } = useUploadImage();
  const src = fromFormValue(value);

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        onChange({}); // temp set as empty to fail validation (i.e. prevent submit while uploading)
        const { file: resizedFile, url } = await onUploadImage(file);
        onChange(toFormValue(resizedFile, url));
      } catch (e) {
        if (e instanceof Error) {
          onError(e.message);
        }
      }
    },
    [onChange, onError, onUploadImage, toFormValue],
  );

  const handleReset = () => {
    onChange(null); // use null since undefined defaults to DEFAULT_VALUE
  };

  if (src) {
    return <Preview image={src} onReset={handleReset} />;
  }

  return (
    <Dropzone
      className={className}
      onError={onError}
      onUpload={handleUpload}
      isUploading={isUploading}
      percentage={percentage}
    />
  );
};

Uploader.displayName = 'Uploader';

export default withControl(Uploader);
